import { Head, Link, useForm, usePage, usePoll } from "@inertiajs/react";
import { AlertCircle, Brain, Building, Calendar, CheckCircle, ClipboardCheck, Clock, DollarSign, FileText, LoaderCircle, PackagePlus, Plus, Sparkles, Wand2, X } from "lucide-react";
import { useEffect, useMemo } from "react";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import QuickProcessingBanner from "@/components/quick-request/quick-processing-banner";
import { SearchableSelect } from "@/components/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import AppLayout from "@/layouts/app-layout";
import { finalizeQuickPurchaseRequestPath, purchaseRequestsPath, quickCreatePurchaseRequestsPath, quickNewPurchaseRequestsPath, quickStartPurchaseRequestsPath } from "@/routes";
import type { BreadcrumbItem, PurchaseRequest, QuickRequestDraftItem, QuickRequestForm, RequestItem, SharedData } from "@/types";
import { getTodayDateString } from "@/utils/date";

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Solicitudes Compras", href: "/purchase_requests" },
  { title: "Crear por texto", href: "/purchase_requests/quick_new" },
];

type SectorOption = NonNullable<PurchaseRequest['sector']>;

interface QuickNewServerPurchaseRequest extends Pick<
  PurchaseRequest,
  | 'id'
  | 'request_number'
  | 'status'
  | 'estimated_cost'
  | 'needed_by_on'
  | 'raw_input_text'
  | 'general_observations'
> {
  sector?: SectorOption | null;
}

interface QuickNewServerRequestItem {
  id: RequestItem['id'];
  raw_description?: RequestItem['raw_description'] | null;
  description?: string | null;
  quantity: RequestItem['quantity'];
  unit: RequestItem['unit'];
  product?: RequestItem['product'] | null;
}

interface QuickNewProps {
  sectors: SectorOption[];
  data?: {
    purchase_request?: QuickNewServerPurchaseRequest;
    request_items?: QuickNewServerRequestItem[];
  };
}

export default function QuickNew({ sectors, data: serverData }: QuickNewProps) {
  // Build initial form state from server (if draft exists)
  const draft = serverData?.purchase_request;
  const initialItems: QuickRequestDraftItem[] = (serverData?.request_items ?? []).map((ri) => ({
    rawDescription: ri.raw_description ?? ri.description ?? "",
    quantity: Number(ri.quantity === 0 || ri.quantity == null ? 1 : ri.quantity),
    unit: (ri.unit ?? '') === '' ? "UN" : ri.unit,
    suggestedProductId: ri.product?.id ?? null,
    suggestedProductName: ri.product?.normalized_name ?? null,
    status: ri.product ? "ok" : "review",
    productId: ri.product?.id ?? null,
  }));

  const { data, setData, post, processing: submitting, transform } = useForm<{
    quick_request: QuickRequestForm;
  }>({
    quick_request: {
      sectorId: draft?.sector?.id ?? null,
      neededBy: draft?.needed_by_on ?? null,
      estimatedTotal: (draft?.estimated_cost as number | null) ?? null,
      rawInputText: draft?.raw_input_text ?? "",
      generalObservation: (draft?.general_observations as string | null) ?? null,
      items: initialItems,
    },
  });

  const items = data.quick_request.items;

  // Sync form items from server when job completes or data changes
  useEffect(() => {
    const serverItems = (serverData?.request_items ?? []).map((ri) => ({
      rawDescription: ri.raw_description ?? ri.description ?? "",
      quantity: Number(ri.quantity === 0 || ri.quantity == null ? 1 : ri.quantity),
      unit: (ri.unit ?? '') === '' ? "UN" : ri.unit,
      suggestedProductId: ri.product?.id ?? null,
      suggestedProductName: ri.product?.normalized_name ?? null,
      status: ri.product ? "ok" : "review",
      productId: ri.product?.id ?? null,
    })) as QuickRequestDraftItem[];

    const serverCount = serverItems.length;
    const currentCount = items.length;

    // Update when we have new items from job or first load of draft-completed
    if (serverCount > 0 && serverCount !== currentCount) {
      setData("quick_request", {
        ...data.quick_request,
        items: serverItems,
        generalObservation: (draft?.general_observations as string | null) ?? data.quick_request.generalObservation ?? null,
      });
    }
  }, [serverData?.request_items, draft?.status]);

  // Units from backend (shared via Inertia). Fallback to UN only
  const shared = usePage().props as SharedData;
  const unitOptions = shared.enums?.units ?? [
    { label: 'UN - Unidades', value: 'UN' },
  ];

  // Derived stats
  const stats = useMemo(() => {
    const total = items.length;
    const ok = items.filter((i) => i.status === "ok").length;
    const review = items.filter((i) => i.status === "review").length;
    const unmatched = items.filter((i) => i.status === "unmatched").length;
    return { total, ok, review, unmatched };
  }, [items]);

  const addEmptyRow = () => {
    setData("quick_request", {
      ...data.quick_request,
      items: [
        ...items,
        {
          rawDescription: "",
          quantity: 1,
          unit: unitOptions[0]?.value ?? "UN",
          suggestedProductId: null,
          suggestedProductName: null,
          status: "review",
          productId: null,
        },
      ],
    });
  };

  const removeRow = (idx: number) => {
    const next = items.slice();
    next.splice(idx, 1);
    setData("quick_request", { ...data.quick_request, items: next });
  };

  const updateItem = (idx: number, patch: Partial<QuickRequestDraftItem>) => {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    setData("quick_request", { ...data.quick_request, items: next });
  };

  const fetchProductOptions = async (term: string) => {
    const res = await fetch(`/products/search?term=${encodeURIComponent(term)}`);
    const json = (await res.json()) as unknown as { id: number; value: number; label: string }[];
    return json.map((p) => ({ value: p.id, label: p.label })) as AsyncOption[];
  };

  // Inertia polling: if draft exists and is processing, keep reloading current page
  const isDraftProcessing = !!draft && draft.status === 'quick_processing';
  const canReprocess = !!draft && draft.status !== 'quick_processing';
  // Ocultar tabla si hay cualquier procesamiento (submit o job en background)
  const isAnyProcessing = isDraftProcessing || submitting;
  // Cantidad de filas para Skeleton: si conocemos cantidad de ítems del server o del form, úsala; fallback a 3
  const skeletonRows = Math.max(
    (serverData?.request_items?.length ?? 0) || items.length || 0,
    3
  );
  const { start, stop } = usePoll(1500, {}, { autoStart: false });
  useEffect(() => {
    if (isDraftProcessing) start(); else stop();
  }, [isDraftProcessing]);


  // Detect query param `id` to show "Crear otra solicitud" button
  const { url } = usePage();
  const hasIdQueryParam = useMemo(() => {
    const qIndex = url.indexOf("?");
    if (qIndex === -1) return false;
    const params = new URLSearchParams(url.slice(qIndex + 1));
    return params.has("id");
  }, [url]);

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Solicitud por texto" />
      <div className="h-full bg-slate-50/30">
        <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <h1 className="text-3xl font-light tracking-tight text-slate-900">Crear solicitud por texto</h1>
              <p className="text-slate-600">Procesamiento inteligente de pedidos con IA</p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3">
              {hasIdQueryParam && (
                <Button asChild variant="outline">
                  <Link href={quickNewPurchaseRequestsPath()}>
                    <Plus className="h-4 w-4 mr-2" /> Nueva solicitud
                  </Link>
                </Button>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="bg-slate-100 text-slate-700 border-0">Total: {stats.total}</Badge>
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                  <CheckCircle className="h-3 w-3 mr-1" />{stats.ok}
                </Badge>
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50">
                  <Clock className="h-3 w-3 mr-1" />{stats.review}
                </Badge>
                <Badge className="bg-red-50 text-red-700 border-red-200 hover:bg-red-50">
                  <AlertCircle className="h-3 w-3 mr-1" />{stats.unmatched}
                </Badge>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-slate-600" />
                  <CardTitle>Configuración de solicitud</CardTitle>
                </div>
                <CardDescription>Complete los datos opcionales y proporcione el texto del pedido</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                    <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">1</div>
                    <span className="text-sm font-medium text-slate-700">Datos opcionales</span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <Building className="h-4 w-4" /> Obra/Sector
                    </Label>
                    <SearchableSelect
                      options={sectors.map((s) => ({ value: String(s.id), label: s.name }))}
                      value={data.quick_request.sectorId ? String(data.quick_request.sectorId) : ""}
                      onValueChange={(val) => setData("quick_request", { ...data.quick_request, sectorId: val ? Number(val) : null })}
                      placeholder="Seleccionar sector"
                    />
                    <p className="text-xs text-slate-500">Facilita la priorización del comprador</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <Calendar className="h-4 w-4" /> Fecha requerida
                    </Label>
                    <Input
                      type="date"
                      min={getTodayDateString()}
                      value={data.quick_request.neededBy ?? ''}
                      onChange={(e) => setData("quick_request", { ...data.quick_request, neededBy: e.target.value === '' ? null : e.target.value })}
                      className="border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                    />
                    <p className="text-xs text-slate-500">Fecha límite para obtener los productos</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <DollarSign className="h-4 w-4" /> Presupuesto estimado
                    </Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-500">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className="pl-8 border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                        value={data.quick_request.estimatedTotal ?? ''}
                        onChange={(e) => setData("quick_request", { ...data.quick_request, estimatedTotal: e.target.value === '' ? null : Number(e.target.value) })}
                      />
                    </div>
                    <p className="text-xs text-slate-500">Costo aproximado para referencia</p>
                  </div>
                </div>

                <div className="lg:col-span-3 lg:border-l lg:border-slate-100 lg:pl-8">
                  <div className="flex items-center gap-2 pb-4 border-b border-slate-100">
                    <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">2</div>
                    <span className="text-sm font-medium text-slate-700">Contenido del pedido</span>
                  </div>
                  
                  <div className="mt-6 space-y-4">
                    <Label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <Wand2 className="h-4 w-4" /> Texto del pedido
                    </Label>
                    <TextareaWithCounter
                      rows={10}
                      placeholder={`Escriba cada ítem en una línea separada o use punto y coma\nIncluya cantidad, unidad, producto y especificaciones\nEspecifique marca o código cuando sea necesario\n\nEjemplos:\n20 bolsas de cemento CPN 40 x 50 kg (Loma Negra)\n15 barras de hierro 8 mm\n50 m de cable unipolar 2,5 mm2 celeste\n1 caja de clavos punta París 2`}
                      value={data.quick_request.rawInputText}
                      onChange={(e) => setData("quick_request", { ...data.quick_request, rawInputText: e.target.value })}
                      disabled={isDraftProcessing}
                      className="border-slate-200 focus:border-slate-400 focus:ring-slate-400 resize-none"
                      maxLength={2000}
                      showCharacterCount={true}
                    />
                    
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <p className="text-xs text-slate-500">Separe los ítems con saltos de línea o punto y coma</p>
                      <Button
                        type="button"
                        onClick={() => {
                          if (canReprocess) {
                            if (!confirm('Al reprocesar se eliminarán todos los ítems detectados anteriormente y se volverán a generar desde el texto.\n\n¿Desea continuar?')) {
                              return;
                            }
                          }
                          
                          transform(() => ({
                            quick_request: {
                              ...(draft ? { purchase_request_id: draft.id } : {}),
                              sector_id: data.quick_request.sectorId,
                              needed_by: data.quick_request.neededBy,
                              estimated_total: data.quick_request.estimatedTotal,
                              raw_input_text: data.quick_request.rawInputText,
                            }
                          }));
                          post(quickStartPurchaseRequestsPath(), { preserveScroll: true });
                        }}
                        disabled={submitting || data.quick_request.rawInputText.trim() === '' || isDraftProcessing}
                      >
                        {submitting ? <LoaderCircle className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
                        {canReprocess ? 'Reprocesar' : 'Procesar con IA'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {draft && (
            <div className="mb-6">
              <QuickProcessingBanner draftId={draft.id} status={draft.status} processing={isDraftProcessing} />
            </div>
          )}

          <div className="mb-8">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <PackagePlus className="h-5 w-5 text-slate-600" />
                  <CardTitle>Ítems procesados</CardTitle>
                </div>
                <CardDescription>
                  <strong className="text-slate-700">Producto sugerido</strong> del catálogo oficial para mayor precisión.
                  <br />
                  <strong className="text-slate-700">Producto inferido por IA</strong> extraído automáticamente del texto.
                </CardDescription>
              </CardHeader>
              <CardContent>
              {isAnyProcessing ? (
                <div className="space-y-4">
                  {Array.from({ length: skeletonRows }).map((_, i) => (
                    <div key={i} className="border border-slate-100 rounded-lg p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                        <div className="lg:col-span-4 space-y-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-9 w-full" />
                        </div>
                        <div className="lg:col-span-2 space-y-2">
                          <Skeleton className="h-4 w-16" />
                          <Skeleton className="h-9 w-full" />
                        </div>
                        <div className="lg:col-span-2 space-y-2">
                          <Skeleton className="h-4 w-16" />
                          <Skeleton className="h-9 w-full" />
                        </div>
                        <div className="lg:col-span-3 space-y-2">
                          <Skeleton className="h-4 w-20" />
                          <Skeleton className="h-9 w-full" />
                        </div>
                        <div className="lg:col-span-1 flex items-end justify-center">
                          <Skeleton className="h-6 w-16" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {items.length === 0 ? (
                    <div className="text-center py-12">
                      <PackagePlus className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 mb-4">No hay ítems para mostrar</p>
                      <Button variant="outline" onClick={addEmptyRow}>
                        <Plus className="h-4 w-4 mr-2" />
                        Agregar ítem manualmente
                      </Button>
                    </div>
                  ) : (
                    items.map((it, idx) => (
                      <div key={idx} className="border border-slate-200 rounded-lg p-6 bg-slate-50/30">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                          <div className="lg:col-span-4 space-y-2">
                            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                              Descripción del producto
                            </Label>
                            <Input
                              value={it.rawDescription}
                              onChange={(e) => updateItem(idx, { rawDescription: e.target.value })}
                              placeholder="Descripción extraída automáticamente"
                              className="border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                            />
                          </div>
                          
                          <div className="lg:col-span-2 space-y-2">
                            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                              Cantidad
                            </Label>
                            <Input 
                              type="number" 
                              inputMode="numeric" 
                              step="0.01" 
                              value={it.quantity} 
                              onChange={(e) => updateItem(idx, { quantity: Number(e.target.value === '' ? 0 : e.target.value) })}
                              className="border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                            />
                          </div>
                          
                          <div className="lg:col-span-2 space-y-2">
                            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                              Unidad
                            </Label>
                            <SearchableSelect
                              options={unitOptions.map((u) => ({ value: u.value, label: u.label }))}
                              value={it.unit}
                              onValueChange={(val) => updateItem(idx, { unit: val === '' ? it.unit : val })}
                              placeholder="Seleccionar unidad"
                              className="h-10"
                            />
                          </div>
                          
                          <div className="lg:col-span-3 space-y-2">
                            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                              Producto del catálogo
                            </Label>
                            <AsyncCombobox
                              value={it.productId ? { value: it.productId, label: it.suggestedProductName ?? "Producto" } : null}
                              onChange={(opt) => updateItem(idx, { productId: opt ? Number(opt.value) : null, suggestedProductName: opt?.label ?? null })}
                              loadOptions={fetchProductOptions}
                              placeholder="Buscar en catálogo"
                            />
                          </div>
                          
                          <div className="lg:col-span-1 flex flex-col items-center justify-between">
                            <div className="mb-2">
                              {it.status === 'ok' && (
                                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Listo
                                </Badge>
                              )}
                              {it.status === 'review' && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50">
                                      <Clock className="h-3 w-3 mr-1" />
                                      Revisar
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    La IA no encontró un producto en el catálogo. Ajusta manualmente.
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {it.status === 'unmatched' && (
                                <Badge className="bg-red-50 text-red-700 border-red-200 hover:bg-red-50">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Pendiente
                                </Badge>
                              )}
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeRow(idx)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {items.length > 0 && (
                    <div className="flex justify-start">
                      <Button variant="outline" onClick={addEmptyRow}>
                        <Plus className="h-4 w-4 mr-2" />
                        Agregar ítem adicional
                      </Button>
                    </div>
                  )}
                </div>
              )}
              </CardContent>
            </Card>
          </div>

          <div className="border-t border-slate-200 sticky bottom-0 z-10 backdrop-blur-sm bg-white/95">
            <div className="mx-auto max-w-7xl px-6 py-6 lg:px-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Sparkles className="h-5 w-5 text-slate-400" />
                  <span>Revisión automática con IA. Puede crear ahora y ajustar posteriormente.</span>
                </div>
                <div className="flex flex-col-reverse lg:flex-row gap-3">
                  <Button asChild variant="outline">
                    <Link href={purchaseRequestsPath()}>Cancelar</Link>
                  </Button>
                  <Button 
                    onClick={() => {
                      // Sin borrador: crear solicitud directa (sin LLM)
                      if (!draft) {
                        transform(() => ({
                          quick_request: {
                            sector_id: data.quick_request.sectorId,
                            needed_by: data.quick_request.neededBy,
                            estimated_total: data.quick_request.estimatedTotal,
                            general_observation: data.quick_request.generalObservation,
                            items: data.quick_request.items.map((it) => ({
                              raw_description: '', // opcional cuando hay producto sugerido
                              quantity: it.quantity,
                              unit: it.unit,
                              product_id: it.productId ?? null,
                            })),
                          }
                        }));
                        post(quickCreatePurchaseRequestsPath(), { preserveScroll: true });
                        return;
                      }

                      // Con borrador: finalizar normalmente
                      transform(() => ({
                        quick_request: {
                          sector_id: data.quick_request.sectorId,
                          needed_by: data.quick_request.neededBy,
                          estimated_total: data.quick_request.estimatedTotal,
                          general_observation: data.quick_request.generalObservation,
                          items: data.quick_request.items.map((it) => ({
                            raw_description: it.rawDescription,
                            quantity: it.quantity,
                            unit: it.unit,
                            product_id: it.productId ?? null,
                          })),
                        }
                      }));
                      post(finalizeQuickPurchaseRequestPath(draft.id), { preserveScroll: true });
                    }} 
                    disabled={submitting || items.length === 0 || draft?.status === 'quick_error'}
                  >
                    {submitting ? <LoaderCircle className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />} 
                    Crear solicitud
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
